<template>
    <SliderRoot
        v-model="sliderValue"
        :min="minYear"
        :max="maxYear"
        :min-steps-between-thumbs="1"
        :step="1"
        class="relative flex h-5 w-full touch-none select-none items-center">
        <SliderTrack class="relative h-[1px] grow rounded-full bg-white">
            <div
                class="absolute bottom-[calc(100%_+_1.75rem)] left-0 flex w-full justify-between">
                <button
                    v-for="year in years"
                    :key="year"
                    type="button"
                    :disabled="disabledYears.includes(year)"
                    :class="{
                        '-translate-y-2.5 scale-125 after:scale-y-125':
                            sliderValue.includes(year) &&
                            !disabledYears.includes(year),
                        'hover:-translate-y-1 hover:scale-110 hover:after:scale-y-110':
                            !sliderValue.includes(year) &&
                            !disabledYears.includes(year),
                        'before:cursor-pointer': !disabledYears.includes(year),
                    }"
                    class="translate-all after:translate-all relative text-16/120 font-semibold duration-300 before:absolute before:left-1/2 before:top-0 before:z-20 before:h-12 before:w-full before:-translate-x-1/2 before:p-5 after:absolute after:left-0 after:top-[calc(100%_+_0.25rem)] after:z-10 after:box-content after:h-6 after:w-1/2 after:overflow-hidden after:border-r after:border-white after:duration-300 disabled:text-white/50">
                    {{ year }}
                </button>
            </div>
            <SliderRange
                :class="{
                    'opacity-0':
                        disabledYears.includes(sliderValue[0]) ||
                        disabledYears.includes(sliderValue[1]),
                }"
                class="translate-all absolute h-1 rounded-full bg-accent duration-300" />
        </SliderTrack>
        <SliderThumb
            aria-label="Volume"
            :class="{
                'opacity-100':
                    sliderValue[0] && !disabledYears.includes(sliderValue[0]),
            }"
            class="translate-all block h-6 w-6 cursor-pointer outline-none duration-300 focus:outline-none">
            <AtomIcon
                name="cromi-icon"
                class="absolute left-1/2 top-1/2 block size-12 -translate-x-1/2 -translate-y-1/2" />
        </SliderThumb>
        <SliderThumb
            aria-label="Volume"
            :class="{
                'opacity-100':
                    sliderValue[1] && !disabledYears.includes(sliderValue[1]),
            }"
            class="translate-all block h-6 w-6 cursor-pointer outline-none duration-300 focus:outline-none">
            <AtomIcon
                name="cromi-icon-reverse"
                class="absolute left-1/2 top-1/2 block size-12 -translate-x-1/2 -translate-y-1/2" />
        </SliderThumb>
    </SliderRoot>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'reka-ui'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'

interface Emits {
    (e: 'update:modelValue', value: [string, string]): void
}

const yearFrom = defineModel<string | null>('yearFrom', {
    default: null,
})

const yearTo = defineModel<string | null>('yearTo', {
    default: null,
})

const props = defineProps({
    years: {
        type: Array as PropType<number[]>,
        default: () => [],
    },
    disabledYears: {
        type: Array as PropType<number[]>,
        default: () => [],
    },
})

const emits = defineEmits<Emits>()
const sliderValue = ref([0, 0])

const minYear = computed<number>(() => {
    return Math.min(...props.years)
})

const maxYear = computed<number>(() => {
    return Math.max(...props.years)
})

function findCloserAvailableYear(
    year: number,
    move: 'next' | 'previous'
): number {
    let closerYear = year
    let area: 'next' | 'previous' = move

    if (props.disabledYears.length === props.years.length) return year

    while (props.disabledYears.includes(closerYear)) {
        let index = props.years.indexOf(closerYear) + (area === 'next' ? 1 : -1)

        if (!props.years[index]) {
            if (area === 'next') {
                area = 'previous'
                index = props.years.indexOf(closerYear) - 1
            } else if (area === 'previous') {
                area = 'next'
                index = props.years.indexOf(closerYear) + 1
            }
        }

        closerYear = props.years[index]
    }

    return closerYear
}

function getDate(date: string) {
    return Number(date.split('-')[0])
}

function init() {
    if (yearFrom.value) {
        sliderValue.value[0] = getDate(yearFrom.value)
    } else {
        sliderValue.value[0] = minYear.value
    }
    if (yearTo.value) {
        sliderValue.value[1] = getDate(yearTo.value)
    } else {
        sliderValue.value[1] = maxYear.value
    }
}

const updateModelValue = useDebounceFn(() => {
    emits('update:modelValue', [
        `${sliderValue.value[0]}-01-01`,
        `${sliderValue.value[1]}-12-31`,
    ])
}, 1000)

watch(sliderValue, () => {
    if (sliderValue.value.length < 2) return

    const newValues = [
        findCloserAvailableYear(sliderValue.value[0], 'previous'),
        findCloserAvailableYear(sliderValue.value[1], 'next'),
    ]

    sliderValue.value[0] = newValues[0]
    sliderValue.value[1] = newValues[1]

    updateModelValue()
})

watch([yearFrom, yearTo], init, { immediate: true })
</script>
